import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import AdaptationTag from "@/components/display/AdaptationTag";
import { PointDetailsPermissions } from "@/features/collection_home/types";
import { MapDisplayPoint } from "@/features/maps/types";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { PeaSummaryResponse } from "@/store/services/facades/types/pea";
import { AdaptationType } from "@/types/adaptation";
import { Rcp } from "@/types/rcp";
import { SelectOutlined } from "@ant-design/icons";
import { Skeleton } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { PathwayWithActions } from "../hooks/useAutoAdaptationPathways";
import { AdaptationErrorRetry } from "./AdaptationErrorRetry";
import AdaptationPathwayComparison, { type PathwayColumnProps } from "./AdaptationPathwayComparison";
import { AdaptationPhysicalRisk } from "./AdaptationPhysicalRisk";
import ResilienceFinancialInsights from "./ResilienceFinancialInsights";
import { MvarDerivedUnavailableNotice } from "./charts/common/MvarDerivedUnavailableNotice";
import { Header } from "./charts/common/Text";
import { isMvarDerivedDataAvailable } from "./charts/common/utils";
import CollectionFiltersLabel from "./CollectionFiltersLabel";
import { HazardBreakdown } from "./HazardBreakdown";
import PathwaySelector from "./PathwaySelector";
import { transformResultToSummary } from "./utils/pointToSummary";
const PathwaySelectHeader = ({
  title,
  description,
  emptyText = "Please select a pathway to view data",
  selectText = "Select a pathway",
  pathways,
  setHighLightedPathwayId
}: {
  title: string;
  description: string;
  emptyText?: string;
  selectText?: string;
  pathways: PathwayWithActions[];
  setHighLightedPathwayId: (pathwayId: string | null) => void;
}) => {
  return <div data-sentry-component="PathwaySelectHeader" data-sentry-source-file="SidebarPointAdaptationDetails.tsx">
      <Header title={<div className="flex items-center gap-2">
            {title}
            <PathwaySelector pathways={pathways.filter(p => p.adaptation_type !== "baseline")} placeholder={selectText} handleSelect={setHighLightedPathwayId} />
          </div>} data-sentry-element="Header" data-sentry-source-file="SidebarPointAdaptationDetails.tsx" />
      <ContextualInfoCollapse className="mt-1" data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="SidebarPointAdaptationDetails.tsx">{description}</ContextualInfoCollapse>

      <div className="h-100 mt-2 flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-gray-400">
        <SelectOutlined className="mb-4 text-2xl text-gray-400" data-sentry-element="SelectOutlined" data-sentry-source-file="SidebarPointAdaptationDetails.tsx" />
        <p>{emptyText}</p>
      </div>
    </div>;
};
const SidebarPointAdaptationDetails = ({
  point,
  regionType,
  year,
  rcp,
  hazard,
  pathways,
  highLightedPathwayId,
  setHighLightedPathwayId = () => null,
  adaptationLoadingAssetId,
  translationPrefixCharts = "portfolios.pointLevel.adaptation",
  translationPrefix = "portfolios.pointLevel.adaptation",
  permissions,
  isLargeSite,
  adaptationError = false,
  retryAdaptations = () => null
}: {
  point: MapDisplayPoint;
  regionType?: string;
  rcp: Rcp;
  year: Year;
  hazard: HazardPlusTotal;
  pathways: PathwayWithActions[];
  highLightedPathwayId: string | null;
  setHighLightedPathwayId: (id: string | null) => void;
  adaptationLoadingAssetId: string | null;
  translationPrefixCharts?: string;
  translationPrefix?: string;
  permissions: PointDetailsPermissions;
  isLargeSite: boolean;
  adaptationError?: boolean;
  retryAdaptations?: () => void;
}) => {
  const {
    t: tHazardBreakdown
  } = useTranslation("tools", {
    keyPrefix: `${translationPrefix}.hazardBreakdown`
  });
  const {
    t: tAdaptationRoi
  } = useTranslation("tools", {
    keyPrefix: `${translationPrefix}.adaptationInsights`
  });
  const {
    t: tAdaptationInsights
  } = useTranslation("tools", {
    keyPrefix: translationPrefix
  });
  const selectedPathway: PathwayWithActions | undefined = pathways.find(pathway => pathway.id === highLightedPathwayId);
  const {
    pathwayColumns,
    pathwayDataList,
    baselineSummary,
    adaptedSummary,
    comparisonSummary
  } = useMemo(() => {
    const columns: PathwayColumnProps[] = [];
    const dataList: {
      id: string;
      adaptationType: AdaptationType;
      data: PeaSummaryResponse;
    }[] = [];
    let baseline: PeaSummaryResponse | null = null;
    let adapted: PeaSummaryResponse | null = null;
    let selected: PeaSummaryResponse | null = null;
    for (const pathway of pathways) {
      // Assumes all pathways only have a single action. TODO: update for multi pathway actions
      const action = pathway.actions?.[0];
      if (!action?.result?.results) continue;
      const summary = transformResultToSummary(point, action, regionType);
      dataList.push({
        id: pathway.id,
        adaptationType: pathway.adaptation_type,
        data: summary
      });
      const row = summary.find(r => r.climate_scenario === rcp && r.hazard === hazard) ?? null;
      columns.push({
        id: action.uuid,
        pathwayId: pathway.id,
        adaptationType: pathway.adaptation_type,
        ttip: row?.ttip?.[year] ?? 0,
        avg_failure_mtd: row?.avg_failure_mtd?.[year] ?? 0,
        productivity_loss: row?.productivity_loss?.[year] ?? 0,
        avg_damage_mtd: row?.avg_damage_mtd?.[year] ?? 0
      });
      if (pathway.adaptation_type === "baseline") baseline = summary;
      if (pathway.adaptation_type === "enhanced_resilience") adapted = summary;
      if (pathway.id === highLightedPathwayId) selected = summary;
    }
    return {
      pathwayColumns: columns,
      pathwayDataList: dataList,
      baselineSummary: baseline,
      adaptedSummary: adapted,
      comparisonSummary: selected
    };
  }, [pathways, point, regionType, rcp, hazard, year, highLightedPathwayId]);
  if (adaptationLoadingAssetId === point.asset?.uuid) {
    return <Skeleton loading active />;
  }
  if (adaptationError) {
    return <AdaptationErrorRetry onRetry={retryAdaptations} />;
  }
  const showHazardCompare = !!selectedPathway && selectedPathway.adaptation_type !== "baseline" && !!baselineSummary;
  const renderRoiInsights = () => {
    if (!selectedPathway || !baselineSummary) return;
    if (selectedPathway.adaptation_type === "baseline" && adaptedSummary) return <ResilienceFinancialInsights id="adaptation-insights-point-level" isLargeSite={isLargeSite} baselineSummary={baselineSummary} comparisonSummary={adaptedSummary} comparisonType="enhanced_resilience" compareResultsMode={selectedPathway.adaptation_type} showDescriptionContext isPointLevel />;
    if (comparisonSummary) return <ResilienceFinancialInsights id="adaptation-insights-point-level" isLargeSite={isLargeSite} baselineSummary={baselineSummary} comparisonSummary={comparisonSummary} comparisonType={selectedPathway.adaptation_type} compareResultsMode={selectedPathway.adaptation_type} showDescriptionContext isPointLevel />;
  };
  const tag = selectedPathway?.adaptation_type && <AdaptationTag type={selectedPathway.adaptation_type} />;
  return <>
      <div>
        <div className="flex items-center gap-2">
          <Header title={tAdaptationInsights("pathwayComparison.title")} data-sentry-element="Header" data-sentry-source-file="SidebarPointAdaptationDetails.tsx" />
          {tag}
        </div>
        <ContextualInfoCollapse className="mb-2 mt-1" data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="SidebarPointAdaptationDetails.tsx">
          {tAdaptationInsights("pathwayComparison.description")}
        </ContextualInfoCollapse>
        {!isMvarDerivedDataAvailable(hazard) && <MvarDerivedUnavailableNotice message={tAdaptationInsights("assetRisk.charts.mvar.unavailableMessage")} className="mb-2 rounded-lg" />}
        <div className="rounded-2xl border border-gray-100 bg-surface pb-2 pt-4 shadow-sm">
          <div className="px-2 text-right">
            <CollectionFiltersLabel withIcons data-sentry-element="CollectionFiltersLabel" data-sentry-source-file="SidebarPointAdaptationDetails.tsx" />
          </div>
          <AdaptationPathwayComparison pathwayColumns={pathwayColumns} regionType={regionType} hazard={hazard} highLightedPathwayId={highLightedPathwayId} setHighLightedPathwayId={setHighLightedPathwayId} data-sentry-element="AdaptationPathwayComparison" data-sentry-source-file="SidebarPointAdaptationDetails.tsx" />
        </div>
      </div>
      {pathwayDataList.length > 0 && <AdaptationPhysicalRisk id="physical-risk-sidebar" dataList={pathwayDataList} highlightedId={highLightedPathwayId} onHighlightChange={setHighLightedPathwayId} translationPrefix="portfolios.pointLevel.adaptation.assetRisk" permissions={permissions} tag={tag} />}
      {!selectedPathway && <PathwaySelectHeader title={tHazardBreakdown("title")} description={tHazardBreakdown("description")} selectText={tHazardBreakdown("pathwaySelection.title") ?? ""} emptyText={tHazardBreakdown("pathwaySelection.description") ?? ""} pathways={pathways} setHighLightedPathwayId={setHighLightedPathwayId} />}
      {selectedPathway && comparisonSummary && <HazardBreakdown id="hazard-breakdown-sidebar-adaptation" data={showHazardCompare && baselineSummary ? baselineSummary : comparisonSummary} compareData={showHazardCompare ? comparisonSummary : undefined} compareResultsMode={showHazardCompare ? "compare" : "baseline"} comparisonType={selectedPathway.adaptation_type} showBreakdownTable translationPrefixCharts={`${translationPrefixCharts}.hazardBreakdown`} translationPrefix={`${translationPrefix}.hazardBreakdown`} isLargeSite={isLargeSite} />}
      {selectedPathway ? renderRoiInsights() : <PathwaySelectHeader title={tAdaptationRoi("title")} description={tAdaptationRoi("description")} selectText={tAdaptationRoi("pathwaySelection.title") ?? ""} emptyText={tAdaptationRoi("pathwaySelection.description") ?? ""} pathways={pathways} setHighLightedPathwayId={setHighLightedPathwayId} />}
    </>;
};
export default SidebarPointAdaptationDetails;